import { useEffect, useState } from "react";
import { X } from "@fg/icons";
import { updateSettings, useSettings, type MeetingSettings } from "@/lib/settings";
import { cn } from "@/lib/utils";

type Tab = "notifications" | "general";

/**
 * Teams' Settings: Notifications (sound, desktop banners, previews, quiet hours) and General (Enter to send).
 * The choices live in this browser; quiet hours are the account-wide schedule and open their own dialog.
 */
export function SettingsDialog({ onClose, onOpenQuietHours }: { onClose: () => void; onOpenQuietHours: () => void }) {
    // Escape closes the dialog wherever focus is.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    const settings = useSettings();
    const [tab, setTab] = useState<Tab>("notifications");
    const [permission, setPermission] = useState<NotificationPermission | "unsupported">(
        typeof Notification === "undefined" ? "unsupported" : Notification.permission
    );

    const askPermission = async () => {
        if (typeof Notification === "undefined") return;
        try { setPermission(await Notification.requestPermission()); } catch { /* blocked */ }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Settings"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
            onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
        >
            <div className="flex h-[min(30rem,90vh)] w-full max-w-2xl overflow-hidden rounded-lg border bg-card shadow-2xl">
                <nav className="w-44 shrink-0 border-r bg-muted/40 p-2" aria-label="Settings sections">
                    <h2 className="px-2 pb-2 pt-1 text-sm font-semibold">Settings</h2>
                    {([["notifications", "Notifications"], ["general", "General"]] as const).map(([id, label]) => (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setTab(id)}
                            aria-current={tab === id ? "page" : undefined}
                            className={cn("block w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent", tab === id && "bg-accent font-medium")}
                        >
                            {label}
                        </button>
                    ))}
                </nav>
                <div className="flex min-w-0 flex-1 flex-col">
                    <header className="flex items-center justify-between border-b px-4 py-2.5">
                        <h3 className="text-sm font-semibold">{tab === "notifications" ? "Notifications" : "General"}</h3>
                        <button type="button" onClick={onClose} aria-label="Close settings" className="rounded p-1 hover:bg-accent"><X className="h-4 w-4" /></button>
                    </header>
                    <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-4">
                        {tab === "notifications" ? (
                            <>
                                <Row id="sound" label="Play a sound" hint="A short chime for new messages." value={settings.sound} onChange={(v) => updateSettings({ sound: v })} />
                                <Row id="banners" label="Show desktop banners" hint="When this tab isn't in front." value={settings.banners} onChange={(v) => updateSettings({ banners: v })} />
                                <Row id="previews" label="Show message previews" hint="Off: banners say only “New message”." value={settings.previews} onChange={(v) => updateSettings({ previews: v })} />
                                <div className="rounded-md border p-3 text-sm">
                                    <p className="font-medium">Browser permission</p>
                                    <p className="mt-0.5 text-xs text-muted-foreground">
                                        {permission === "granted" ? "Allowed: banners can appear." : permission === "denied" ? "Blocked in your browser's site settings." : permission === "unsupported" ? "This browser doesn't support banners." : "Banners need your permission."}
                                    </p>
                                    {permission === "default" ? (
                                        <button type="button" onClick={() => void askPermission()} className="mt-2 rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">Allow banners</button>
                                    ) : null}
                                </div>
                                <div className="rounded-md border p-3 text-sm">
                                    <p className="font-medium">Quiet hours</p>
                                    <p className="mt-0.5 text-xs text-muted-foreground">Pause notifications on a schedule.</p>
                                    <button type="button" onClick={() => { onClose(); onOpenQuietHours(); }} className="mt-2 rounded-md border px-3 py-1 text-xs font-medium hover:bg-accent">Set quiet hours…</button>
                                </div>
                            </>
                        ) : (
                            <Row id="enterToSend" label="Enter sends the message" hint="Off: Ctrl+Enter sends and Enter starts a new line." value={settings.enterToSend} onChange={(v) => updateSettings({ enterToSend: v })} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ id, label, hint, value, onChange }: { id: keyof MeetingSettings; label: string; hint: string; value: boolean; onChange: (v: boolean) => void }) {
    return (
        <label htmlFor={`set-${id}`} className="flex cursor-pointer items-center justify-between gap-4 rounded-md px-1 py-2 hover:bg-accent/40">
            <span>
                <span className="block text-sm font-medium">{label}</span>
                <span className="block text-xs text-muted-foreground">{hint}</span>
            </span>
            <input id={`set-${id}`} type="checkbox" role="switch" checked={value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 shrink-0 accent-[var(--primary,#2563eb)]" />
        </label>
    );
}
